import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { FormikHelpers } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import {
  ResetBudgetForSubaccountMutation,
  ResetBudgetForSubaccountMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  FormikDialog,
  FormikSubmitButton,
  FormikTextField,
  submitErrorField,
} from "~frontend/components/forms";
import {
  GET_LATEST_BUDGETS,
  RESET_BUDGET_FOR_SUBACCOUNT,
} from "~frontend/graphql/budgets";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { BudgetCalculator } from "./BudgetCalculator";
import { DollarAdornment } from "./DollarAdornment";
import { Subaccount } from "./types";

type ResetBudgetFormValues = {
  allocation: string;
  warningThreshold: string;
  [submitErrorField]: any;
};

export function ResetBudgetDialog({
  subaccount,
  onClose,
}: {
  subaccount: Subaccount;
  onClose: () => void;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();

  const [resetBudget] = useMutation<
    ResetBudgetForSubaccountMutation,
    ResetBudgetForSubaccountMutationVariables
  >(RESET_BUDGET_FOR_SUBACCOUNT, {
    onErrorSnackbarMessage: "Error editing budget",
    refetchQueries: [GET_LATEST_BUDGETS],
    awaitRefetchQueries: true,
  });

  const handleSubmit = async (
    values: ResetBudgetFormValues,
    formik: FormikHelpers<ResetBudgetFormValues>
  ) => {
    const allocationInMillicents = Math.floor(
      parseFloat(values.allocation) * 100 * 1000
    );
    const warningThresholdInMillicents = Math.floor(
      parseFloat(values.warningThreshold) * 100 * 1000
    );

    const { errors } = await resetBudget({
      variables: {
        input: {
          subaccountId: subaccount.id,
          allocationInMillicents,
          warningThresholdInMillicents,
        },
      },
    });

    if (errors) {
      formik.setErrors({ [submitErrorField]: getErrorMessage(errors) });
    } else {
      onClose();
    }
  };

  return (
    <FormikDialog
      open
      initialValues={{
        [submitErrorField]: null,
        allocation: "",
        warningThreshold: "",
      }}
      validationSchema={Yup.object().shape({
        allocation: Yup.number()
          .typeError("Budget must be a number")
          .min(0)
          .required("Budget is required"),
        warningThreshold: Yup.number()
          .typeError("Warning threshold must be a number")
          .test(
            "less-than-budget",
            "Warning threshold must be less than budget",
            function (value) {
              const { allocation } = this.parent;
              return (value ?? 0) < allocation;
            }
          )
          .required("Warning threshold is required"),
      })}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid, errors }) => {
        return (
          <>
            <DialogTitle>
              Reset Budget for <strong>{subaccount.name}</strong>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ marginBottom: "16px" }}>
                Resetting your budget on this subaccount will end the current
                budgeting period and start a new one, beginning today. The
                remaining balance in your old budget and all spending before
                today will not be counted towards your new budgeting period.
              </Typography>
              <FormikTextField
                name="allocation"
                label="New Budget"
                InputProps={{
                  startAdornment: <DollarAdornment />,
                }}
                fullWidth
              />
              <BudgetCalculator sx={{ marginBottom: "16px" }} />
              <FormikTextField
                name="warningThreshold"
                label="Warning Threshold"
                bottomLabel="The Subaccount and Organization Admins will get an email warning when your balance falls below this amount"
                InputProps={{
                  startAdornment: <DollarAdornment />,
                }}
                fullWidth
              />
            </DialogContent>
            <DialogActions
              sx={{
                justifyContent: "space-between",
                pt: 0,
                px: "24px",
                pb: "20px",
              }}
            >
              <Box sx={{ width: "250px" }}>
                {errors[submitErrorField] && (
                  <Typography variant="body2" sx={{ color: palette.red }}>
                    {errors[submitErrorField]}
                  </Typography>
                )}
              </Box>
              <FlexBox gap="10px">
                <Button
                  disabled={isSubmitting}
                  variant="contained"
                  color="tertiary"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <FormikSubmitButton
                  disabled={!isValid}
                  submitText="Reset Budget"
                  loadingText="Saving..."
                />
              </FlexBox>
            </DialogActions>
          </>
        );
      }}
    </FormikDialog>
  );
}
